import { useMemo } from 'react'
import { connect, ConnectedProps } from 'react-redux'
import ModalCESPRule from './ModalCESPRule'
import SortSpecificPriceRules from './SortSpecificPriceRules'
import { SpecificPriceRuleModelType } from '@teinor/erp/types/company/product/specificPriceRule'
import { ComponentWithPermissions } from '../../../../../../baseComponents/CreateRoutering/types'
import { permissionCheck } from '../../../../../../baseComponents/PermissionChecker/function'
import TwinTable from '../../../../../../baseComponents/TwinTable'
import BaseScreenTable from '../../../../../../baseComponents/TwinTable/BaseScreenTable'
import { TableComponentProps } from '../../../../../../baseComponents/TwinTable/BaseScreenTable/types'
import { CustomRenderString, DefaultTableCustomRender, CustomRenderBoolean, EditRow, DeleteRow } from '../../../../../../baseComponents/TwinTable/VirtualTableListing/CustomRenders'
import { ColumnTableSchema } from '../../../../../../baseComponents/TwinTable/VirtualTableListing/Subcomponents/types'
import { dictionaryComplexFromJsonArr } from '../../../../../../utils/globals/dictionary'
import withLoading from '../../../../../../utils/hoc/withLoading'
import useTwinTranslation from '../../../../../../utils/hooks/useTwinTranslation'
import { AllReduxPayloads } from '../../../../../../utils/reducers'
import { CustomerGroupPayload } from '../../../../../../utils/reducers/customers/customerGroups'
import { getActiveCurrenciesDictionary } from '../../../../../../utils/reducers/getters'
import { PriceZoneGroupsPayload } from '../../../../../../utils/reducers/globals/priceZoneGroups'
import ModalDeleteSPRule from './ModalDeleteSPRule'
import useCustomerTypesDict from '../../../../../../utils/hooks/customer/useCustomerTypesDict'

interface SpecificPriceRulesProps extends ComponentWithPermissions { }

const SpecificPriceRules: React.FC<SpecificPriceRulesProps> = ({ userPermissions }) => {
    const { t } = useTwinTranslation()

    const pageData = {
        title: t('rateGroups', 'Grupos de tarifas'),
        newText: t('createNewGroup', 'Crear nuevo grupo')
    }

    return (
        <BaseScreenTable TableComponent={TableConnect} CreateModalComponent={ModalCESPRule} EditModalComponent={ModalCESPRule} DeleteModalComponent={ModalDeleteSPRule} pageData={pageData} userPermissions={userPermissions} />
    )
}

type SpecificPriceRulesTableProps = TableComponentProps & ReduxSpecificPricesTable & {}

const SpecificPriceRulesTable: React.FC<SpecificPriceRulesTableProps> = ({ tableInstance, setTableInstance, userPermissions, setOpened, priceZoneGroups, customerGroups }) => {
    const memorizedCurrencies = useMemo(() => getActiveCurrenciesDictionary(), [])
    const priceZoneGroupsDict = useMemo(() => dictionaryComplexFromJsonArr(priceZoneGroups || {}), [priceZoneGroups])
    const { t } = useTwinTranslation()
    const { dictCustomerType } = useCustomerTypesDict()
    const columns: ColumnTableSchema<SpecificPriceRuleModelType> = [
        {
            id: 'id',
            dataKey: 'id',
            label: t('id', 'ID'),
            minWidth: 80,
            sortable: true,
            customRender: (parameterValue) => <CustomRenderString value={'T' + parameterValue} />
        },
        {
            id: 'name',
            dataKey: 'name',
            label: t('name', 'Nombre'),
            minWidth: 130,
            sortable: true,
            searchableComponent: {
                component: 'SearchableComponentBasic',
                extraComponentData: { label: t('name', 'Nombre') }
            }
        },
        {
            id: 'CurrencyId',
            dataKey: 'CurrencyId',
            label: t('currency', 'Moneda'),
            customRender: (parameterValue) => <DefaultTableCustomRender items={memorizedCurrencies} value={parameterValue} />,
            searchableComponent: {
                component: 'SearchableComponentsCustomSelectSearchBar',
                extraComponentData: { items: memorizedCurrencies, label: t('currency', 'Moneda') }
            },
            generalSearchable: false
        },
        {
            id: 'formula',
            dataKey: 'formula',
            label: t('formula', 'Fórmula'),
            minWidth: 90,
        },
        {
            id: 'decimals',
            dataKey: 'decimals',
            minWidth: 85,
            label: t('decimals', 'Decimales'),
        },
        {
            id: 'rounding',
            dataKey: 'rounding',
            minWidth: 85,
            label: t('rounding', 'Redondeo'),
        },
        {
            id: 'customerType',
            dataKey: 'customerType',
            minWidth: 70,
            label: t('type', 'Tipo'),
            customRender: (parameterValue) => <CustomRenderString value={dictCustomerType[parameterValue]?.name} />
        },
        {
            id: 'PriceZoneGroupId',
            dataKey: 'PriceZoneGroupId',
            label: t('priceZoneGroup', 'Zona de precio'),
            minWidth: 120,
            customRender: (parameterValue) => <DefaultTableCustomRender items={priceZoneGroupsDict} value={parameterValue} />,
        },
        {
            id: 'SpecificPriceCustomersRelsParsed',
            dataKey: 'SpecificPriceCustomersRelsParsed',
            label: t('customerGroups', 'Grupos de cliente'),
            minWidth: 130,
            customRender: (parameterValue) => <DefaultTableCustomRender items={customerGroups?.dictionary || {}} value={parameterValue} fieldName='customName' />
        },
        {
            id: 'with_vat',
            dataKey: 'with_vat',
            label: t('priceWithVatShort', 'Precio con imp.'),
            minWidth: 120,
            customRender: (parameterValue) => <CustomRenderBoolean value={String(parameterValue) === 'true'} width={95} />
        },
        {
            id: 'edit',
            dataKey: 'id',
            label: '',
            width: 50,
            simpleLabel: t('edit', 'Editar'),
            customRender: (parameterValue, allRowData) => <EditRow key={'edit' + parameterValue} {...{ parameterValue, allRowData }} />
        },
        {
            id: 'delete',
            dataKey: 'id',
            label: '',
            width: 50,
            simpleLabel: t('delete', 'Eliminar'),
            customRender: (parameterValue, allRowData) => <DeleteRow key={'delete' + parameterValue} {...{ parameterValue, allRowData }} onClick={(_idk, allRowData) => setOpened({ type: 'delete', allRowData })} />,
            havePermission: permissionCheck(userPermissions, 'delete')
        }
    ]

    const children = useMemo(() => <SortSpecificPriceRules tableInstance={tableInstance} userPermissions={userPermissions} />, [tableInstance, userPermissions])
    return (
        <TwinTable name='allSpecificPriceRulesC' columns={columns} getDataFrom='/api/app/product/specificPrice/getAllSpecificPriceRules' defaultOrder={{ defaultOrderField: 'order', order: 'asc' }} setTableInstance={setTableInstance} onRowClick={(_id, rowData) => setOpened({
            type: 'edit',
            allRowData: rowData
        })} >
            {children}
        </TwinTable>
    )
}

const specificPricesTableDispatch = {
    setCustomerGroups: (payload: CustomerGroupPayload) => ({ type: 'CHANGE_CUSTOMER_GROUP', payload }),
    setPriceZoneGroups: (payload: PriceZoneGroupsPayload) => ({ type: 'CHANGE_PRICEZONEGROUPS', payload }),
}

type ReduxSpecificPricesTable = ConnectedProps<typeof specificPricesTableConnect>
const mapSpecificPricesTableConnector = (state: AllReduxPayloads) => ({ customerGroups: state.customerGroups, priceZoneGroups: state.priceZoneGroups })
const specificPricesTableConnect = connect(mapSpecificPricesTableConnector, specificPricesTableDispatch)

const specificPricesTableConnectLoading = withLoading(SpecificPriceRulesTable, [{ fetchUrl: '/api/app/product/priceZoneGroup/getAllPriceZoneGroups', propName: 'priceZoneGroups', setFunctionName: 'setPriceZoneGroups', params: { include: true } }, { fetchUrl: '/api/app/customer/customerGroup/getAllCustomersGroup', propName: 'customerGroups', setFunctionName: 'setCustomerGroups' }])

const TableConnect = specificPricesTableConnect(specificPricesTableConnectLoading);

export default SpecificPriceRules